import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  GridOptions,
  ICellRendererParams,
  ITooltipParams,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { BlankSessionService } from '@helios/sessions'
import {
  RootState,
  selectActiveSessionReconDate,
  selectActualReconDate,
  selectLegacyUserId,
  selectSelectedReconDate,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalOptions,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  ModalLinkRendererParams,
  NgGridComponents,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { omit } from 'lodash-es'
import { Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'
import { BulkTransactionModalInput } from '../../../model/bulk-transaction/bulk-popup-input-model'
import {
  BulkSecurityExchangeDerivedRequest,
  BulkSecurityExchangeRow,
  ExchangeType,
  securityExchangeField,
} from '../../../model/bulk-transaction/bulk-security-exchange/bulk-security-exchange.model'
import {
  BulkSecurityExchangeTransaction,
  MetaDataTransaction,
  TransactionRequestWrapper,
} from '../../../model/bulk-transaction/bulk-transaction'
import { BulkTransactionRequest } from '../../../model/bulk-transaction/bulk-transaction.model'
import { BulkSecurityExchangeValidationView } from '../../../model/bulk-transaction/bulk-validation-view.model'
import { CounterParty } from '../../../model/counter-party.model'
import { InventoryType, inventories } from '../../../model/inventory-type.model'
import {
  Basis,
  MvtBasis,
  MvtBasisSelection,
} from '../../../model/mvt-basis.model'
import {
  normalExchangeTypes,
  statExchangeTypes,
} from '../../../model/single-transaction/direct-transfer/exchange-type.model'
import { MarketValueTransferModel } from '../../../model/single-transaction/market-value-transfer.model'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  RequestMode,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { BulkDeleteService } from '../../../services/bulk-transaction/bulk-delete.service'
import { PasteService } from '../../../services/bulk-transaction/paste.service'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import {
  BULK_DIALOG_EVENT_NAME,
  buildMvtBasisSelection,
  bulkMetricsDetail,
  customSortComparatorBulk,
  customSortComparatorBulkNumeric,
  customSortComparatorBulkValidatorColumn,
  fillHandleDoubleClickHandler,
  numberValueFormatter,
  onSortChange,
} from '../../../util/bulk-specific-common-utils'
import {
  deduceValidityStatusCellClass,
  emptySelectedCellsOnValidKeyPress,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  markRowsMissingAccount,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { BvMvBasisComponent } from '../../bv-mv-basis-selector/bv-mv-basis-selector.component'
import { BulkTransactionModalResponse } from '../bulk-transaction-modal-response'
import { ErrorTooltipComponent } from '../error-Tooltip/error-tooltip.component'
import { BulkTransactionsSaveConfirmationModalComponent } from '../bulk-transactions-confirm-button-modal/bulk-transactions-confirm-button-modal.component'

@Component({
  selector: 'hs-bulk-security-exchange',
  templateUrl: './bulk-security-exchange.component.html',
  styleUrls: ['./bulk-security-exchange.component.css'],
  providers: [PasteService, CopyService, BulkDeleteService],
})
export class BulkSecurityExchangeComponent implements OnInit, OnDestroy {
  @Input() transInput?: BulkTransactionModalInput
  private readonly onPasteEndBinding = this.onPasteEnd.bind(this)
  private readonly adjustTransactionsPostDeleteBinding =
    this.adjustTransactionsPostDelete.bind(this)

  title = 'Security Exchange Bulk Create'
  getFieldValidatorFormatter = getFieldValidatorFormatter
  formSubmitting = false
  allDataValidated = false
  transactionLoaded = false
  deletePostProcessingInProgress = false
  rowData: BulkSecurityExchangeRow[] = []
  colDefs: ColDef[] = this.getColDefs()
  gridOptions: GridOptions = this.createGridOptions()
  cellLevelErrorMessageMap = new Map<string, string[]>()
  cellLevelWarningMessageMap = new Map<string, string[]>()
  userId?: number

  mvtDataPromises: Map<string, Promise<MvtBasis>> = new Map<
    string,
    Promise<MvtBasis>
  >()
  mvtDataLoaded: Map<string, MvtBasis> = new Map<string, MvtBasis>()
  note = new FormControl('')
  loadingBasis = false
  lastPastedRowIds: string[] = []
  lastColumnIdsPasted: Set<string> = new Set<string>()
  reconDate?: Date
  sessionReconDate?: Date
  selectedReconDate?: Date
  isDataValid = false
  isValidating = false
  disableValidate = false
  errorFields: string[] = []
  gridApi?: AgGridApi
  executionId?: number
  externalAccountId?: number
  originalCusip?: string
  noteWarning: ValidationErrors | null = null
  currentNoteError: ValidationErrors | null =
    TransactionConstants.DEFAULT_NOTE_VALIDATION_ERROR
  genericErrors: string[] = []
  genericWarnings: string[] = []
  hasTransactionMatchCustody = false
  hasTransactionMatchTrading = false
  currentCounterParty?: CounterParty
  currentRowIndex = 0
  loadingDialog = false
  sortable = false
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  errorDetails: unknown
  exchangeTypeValues = normalExchangeTypes
  isDeriving = false
  cusipToSecurityMap = new Map()
  isCommonValidationError = false
  disableSaveValidRows = false
  showConfirmButtonModal = false

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    public toastService: ToastService,
    readonly activeModal: NgbActiveModal,
    readonly modalService: NgbModal,
    readonly store: Store<RootState>,
    readonly bulkTransactionService: CreateEditTransactionService,
    private readonly blankSessionService: BlankSessionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly bulkDeleteService: BulkDeleteService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly telemetryService: TelemetryService,
    readonly savedViewService: WorkspaceGridSavedViewService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.userPreferencesService
      .getBulkDialogSortPromptPreference()
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe(sortable => {
        this.sortable = sortable
        if (this.gridOptions.defaultColDef) {
          this.gridOptions.defaultColDef.sortable = sortable
          this.gridOptions.defaultColDef.comparator = customSortComparatorBulk
        }
      })
  }

  async ngOnInit() {
    const startTime = Date.now()
    this.note.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.setNoteErrors()
      })
    await this.init()
    this.note.markAsTouched()
    this.note.setErrors(this.currentNoteError)
    const now = Date.now()
    const [count, type] = bulkMetricsDetail(
      this.rowData.length,
      this.transInput,
    )

    this.telemetryService.emitCustomEvent(BULK_DIALOG_EVENT_NAME, {
      dialogName: 'Security-Exchange-' + type,
      durationMs: now - startTime,
      tranCount: count,
    })
  }

  private async init() {
    this.document.addEventListener('pasteDone', this.onPasteEndBinding)
    this.document.addEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )
    this.loadDataCreate()
    this.createFreshRowData()
    this.gridApi?.api.applyTransaction({ add: this.rowData })
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()

    this.document.removeEventListener('pasteDone', this.onPasteEndBinding)
    this.document.removeEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )
  }

  /** @internal */
  loadDataCreate() {
    this.store
      .select(selectActualReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((sessionReconDate?: Date) => {
        this.sessionReconDate = sessionReconDate
      })
    this.store
      .select(selectSelectedReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((selectedReconDate?: Date) => {
        this.selectedReconDate = selectedReconDate
      })
    this.store
      .select(selectLegacyUserId)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
  }

  setNoteErrors() {
    if (this.currentNoteError != null) {
      this.note.setErrors(this.currentNoteError)
    }
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => {
        this.gridApi = event
        this.pasteService.registerGrid(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.bulkDeleteService.registerGrid(event.api)
        this.savedViewService.manageSavedView(
          event,
          'Bulk-direct-exchange-workspace-key',
          this.destroySubject$,
        )
      },
      getRowNodeId: ({ rowId }: BulkSecurityExchangeRow) => rowId,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      rowSelection: 'multiple',
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandler(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: this.currentRowIndex,
            colKey: event.column.getColId(),
          })
        }
      },

      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      suppressMultiRangeSelection: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      enableFillHandle: true,
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.colDefs,
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
      onCellFocused: (event: CellFocusedEvent) => {
        this.handleCellFocus(event)
      },
      onSortChanged: (params: SortChangedEvent) => onSortChange(params),
    }
  }

  private getColDefs(): ColDef[] {
    return [
      {
        field: securityExchangeField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: params =>
          deduceValidityStatusCellClass(params.data.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessageMap &&
            params.data.errorMessageMap.size > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
        tooltipComponent: ErrorTooltipComponent,
        comparator: customSortComparatorBulkValidatorColumn,
      },
      {
        field: securityExchangeField('rowNumber'),
        sortable: false,
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
      },
      {
        field: securityExchangeField('errorMessageMap'),
        hide: true,
      },
      {
        field: securityExchangeField('accountId'),
        headerName: 'Account ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        width: 120,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('entryDate'),
        headerName: 'Entry Date',
        width: 100,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('fromAssetId'),
        headerName: 'From Asset ID',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('toAssetId'),
        headerName: 'To Asset ID',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },

      {
        field: securityExchangeField('fromUnits'),
        headerName: 'From Units',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('rate'),
        headerName: 'Rate',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },

        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('toUnits'),
        headerName: 'To Units',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('exchangeType'),
        headerName: 'Exchange Type',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Select,
          options: {
            items: statExchangeTypes,
            bindValue: 'name',
            bindLabel: 'name',
          },
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },

      {
        field: securityExchangeField('isConversion'),
        headerName: 'Is Conversion',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Select,
          options: {
            items: ['True', 'False'],
          },
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('inventoryType'),
        headerName: 'Inventory Type',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Select,
          options: {
            items: inventories,
            bindValue: 'name',
            bindLabel: 'name',
          },
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityExchangeField('marketPrice'),
        headerName: 'Market Price',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',

        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },

      {
        field: securityExchangeField('mvtBasisSelectionText'),
        editable: true,
        sortable: true,
        headerName: 'Market Value Transfer',
        width: 225,
        tooltipValueGetter: params => {
          return this.shouldOpenMvtPicker(params?.data)
            ? ''
            : 'Please configure Entry date and Account ID before adjusting basis'
        },
        tooltipComponentParams: {
          showTooltip: params => !this.shouldOpenMvtPicker(params?.data),
        } as NfTooltipParams,
        cellRenderer: undefined,
        cellRendererSelector: (params: ICellRendererParams) => {
          return this.getPopUpRenderer(params)
        },
      },
      {
        field: securityExchangeField('mvtBasisSelection'),
        hide: true,
      },
      {
        field: securityExchangeField('balanceUnits'),
        hide: true,
      },
      {
        field: securityExchangeField('calcTransferPrice'),
        hide: true,
      },
    ]
  }

  handleCellFocus(event: CellFocusedEvent) {
    this.currentRowIndex = event.rowIndex ?? 0
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return [
      {
        name: 'Remove Row(s)',
        tooltip: 'Remove selected row(s)',
        action: () => {
          this.bulkDeleteService.deleteHandler()
        },
      },
      'export',
    ]
  }

  adjustTransactionsPostDelete() {
    this.deletePostProcessingInProgress = true
    const newRowData: BulkSecurityExchangeRow[] = []
    this.gridApi?.api.forEachNode((rowNode, index) => {
      rowNode.data.rowId = (index + 1).toString()
      newRowData.push(rowNode.data)
    })
    this.gridApi?.api.setRowData(newRowData)
    this.deletePostProcessingInProgress = false
  }

  async onPasteEnd(evt: Event) {
    const pasteDetails = (evt as CustomEvent).detail
    this.disableValidate = true
    this.loadingDialog = true

    this.lastPastedRowIds = pasteDetails.pastedRowData.map(
      (rowData: { rowId: BulkSecurityExchangeRow }) => rowData.rowId,
    )
    this.lastColumnIdsPasted = pasteDetails.colPastedSet

    this.applyOnChangeForPastedRowNodes()
    this.loadingDialog = false
    this.disableValidate = false
  }

  applyOnChangeForPastedRowNodes() {
    this.lastPastedRowIds.forEach(rowId => {
      const displayedNode = this.gridApi?.api.getRowNode(rowId)
      if (displayedNode) {
        if (displayedNode.data.mvtBasisSelection === '') {
          displayedNode.data.mvtBasisSelection = []
        }
        if (this.shouldOpenMvtPicker(displayedNode.data)) {
          this.initializeMvtBasis(
            displayedNode,
            !this.lastColumnIdsPasted.has('mvtBasisSelectionText'),
          )
        } else {
          displayedNode.data.mvtBasisSelectionText =
            TransactionConstants.MIN_FIELD_MESSAGE
          displayedNode.data.mvtBasisSelection = []
        }
        this.applyOnChangeForPastedRowNode(displayedNode)
      }
    })
  }

  applyOnChangeForPastedRowNode(displayedNode: RowNode) {
    const allColumns = this.gridApi?.columnApi.getAllColumns()
    allColumns
      ?.filter(column =>
        this.lastColumnIdsPasted.has(column.getColDef().field ?? ''),
      )
      .forEach(currentColumn => {
        const colId = currentColumn.getColDef().field
        this.onColumnValueChanged(
          {
            column: { getColId: () => colId },
            node: displayedNode,
            data: displayedNode.data,
            newValue: displayedNode?.data[colId ?? ''],
            oldValue: '',
          } as unknown as CellValueChangedEvent,
          this.lastColumnIdsPasted,
        )
      })
  }

  showToolTip(params?: ITooltipParams) {
    const impactedFieldColDef = params?.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params?.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (
      (currentValidationErrors && currentValidationErrors?.length > 0) ||
      (currentValidationWarnings && currentValidationWarnings?.length > 0)
    ) {
      return true
    }
    return false
  }

  toolTipValueGetter(params: ITooltipParams): string {
    const impactedFieldColDef = params.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)

    if (currentValidationErrors) {
      return currentValidationErrors.join(' | ')
    } else if (currentValidationWarnings) {
      return currentValidationWarnings.join(' | ')
    } else {
      return impactedFieldColDef?.field == 'counterParty' ? params.value : ''
    }
  }

  deduceImpactedField(impactedFieldFromValidationInfo: string): string {
    if (impactedFieldFromValidationInfo == 'securityId') {
      return 'fromAssetId'
    } else if (impactedFieldFromValidationInfo == 'transferPrice') {
      return 'marketPrice'
    } else if (impactedFieldFromValidationInfo == 'destinationCusip') {
      return 'toAssetId'
    } else if (impactedFieldFromValidationInfo == 'units') {
      return 'fromUnits'
    } else {
      return impactedFieldFromValidationInfo
    }
  }

  getCellStyle(params: CellClassParams): CellStyle {
    const impactedFieldProcessed = this.deduceImpactedField(
      params.colDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    if (this.cellLevelErrorMessageMap.has(cellKey)) {
      return { 'background-color': 'lightcoral' }
    } else if (this.cellLevelWarningMessageMap.has(cellKey)) {
      return { 'background-color': 'yellow' }
    } else {
      return { 'background-color': '#00000000' }
    }
  }

  onColumnValueChanged(
    event: CellValueChangedEvent,
    columnPastedIds?: Set<string>,
  ) {
    if (
      event.newValue == event.oldValue ||
      this.deletePostProcessingInProgress
    ) {
      return
    }
    const rowNode = event.node
    const colId = event.column.getColId()

    if (
      rowNode?.data.validityStatus == 'inactive' &&
      colId != 'validityStatus' &&
      colId != 'errorMessageMap'
    ) {
      if (rowNode.data.inventoryType == '') {
        rowNode.data.inventoryType = 'LONG'
      }
      if (rowNode.data.isConversion == '') {
        rowNode.data.isConversion = 'False'
      }
      if (rowNode.data.exchangeType == '') {
        rowNode.data.exchangeType = normalExchangeTypes[0].name
      }
      if (rowNode.data.rate == '') {
        rowNode.data.rate = 1
      }

      if (
        Number(rowNode?.rowIndex) + 1 ==
        this.gridApi?.api.getDisplayedRowCount()
      ) {
        const rows = [
          this.generateEmptyDataEntry(false, Number(rowNode.data.rowId) + 1),
        ]
        this.gridApi?.api.applyTransaction({ add: rows })
      }
    }
    if (
      colId != 'validityStatus' &&
      colId != 'errorMessageMap' &&
      rowNode.data.validityStatus != 'intermediate'
    ) {
      rowNode.data.validityStatus = 'intermediate'
      this.allDataValidated = false
    }
    switch (colId) {
      case 'entryDate':
      case 'accountId':
        if (!columnPastedIds) {
          if (this.shouldOpenMvtPicker(rowNode.data)) {
            this.initializeMvtBasis(rowNode, true)
          } else {
            rowNode.setDataValue(
              'mvtBasisSelectionText',
              TransactionConstants.MIN_FIELD_MESSAGE,
            )
            rowNode.setDataValue('mvtBasisSelection', [])
          }
        }

        break

      case 'fromUnits':
        if (event.node.data.inventoryType != 'ISSUE' && event.newValue)
          event.node.setDataValue('fromUnits', Math.abs(Number(event.newValue)))
        break
      case 'toUnits':
        if (event.node.data.inventoryType != 'ISSUE' && event.newValue)
          event.node.setDataValue('toUnits', Math.abs(Number(event.newValue)))
        break
      case 'inventoryType':
        if (event.value != 'ISSUE' && event.node.data.fromUnits) {
          event.node.setDataValue(
            'fromUnits',
            Math.abs(Number(event.node.data.fromUnits)),
          )
        }
        if (event.value != 'ISSUE' && event.node.data.toUnits) {
          event.node.setDataValue(
            'toUnits',
            Math.abs(Number(event.node.data.toUnits)),
          )
        }
        break
      case 'mvtBasisSelectionText':
        this.handleMvtSelectedChanged(event.node)
        break
    }
  }

  disableSaveValidRowsButton() {
    let disable = true
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus == 'valid' ||
        node.data.validityStatus == 'warning'
      ) {
        disable = false // If at least one node has validity 'valid' or 'warning', set disable to false
        return // Exit the loop early if validity is 'valid'
      }
    })
    // Set disableSaveValidRows based on the value of disable
    this.disableSaveValidRows = disable
  }

  // Flags active rows with no account as invalid client-side so the user gets an
  // inline "Account is required" error on the cell instead of a silently disabled
  // Validate button. Returns true if any row was missing an account, in which case
  // validate() short-circuits and never sends accountId 0 to the backend.
  markMissingAccountRows(): boolean {
    const anyMissing = markRowsMissingAccount(
      this.gridApi?.api,
      'accountId',
      this.deduceImpactedField('accountId'),
      this.cellLevelErrorMessageMap,
    )
    if (anyMissing) {
      this.isDataValid = false
    }
    return anyMissing
  }

  async deriveAndValidate() {
    if (this.markMissingAccountRows()) {
      return
    }
    this.errorDetails = ''
    await this.populateDerivedFields()
    await this.validate()
  }

  async populateDerivedFields() {
    this.isDeriving = true
    this.loadingText = TransactionConstants.FETCH_DERIVED_FIELDS
    const tranData = this.getGridTransactionsRequestforExchange()

    try {
      const derivedResponse = await this.bulkTransactionService
        .getDerivedFieldsForSecurityExchange({
          exchangeDetailsRequest: tranData,
        })
        .toPromise()
      if (derivedResponse.securityIdMap) {
        this.cusipToSecurityMap = new Map(
          Object.entries(derivedResponse.securityIdMap),
        )
      }
      new Map(Object.entries(derivedResponse.tranDetails)).forEach(
        (value, key) => {
          this.gridApi?.api.getRowNode(key ?? '')
          const currentRow = this.gridApi?.api.getRowNode(key ?? '')?.data
          if (currentRow) {
            if (value.pricingData?.price) {
              if (!currentRow.marketPrice) {
                currentRow.marketPrice = value.pricingData.price
              }
              currentRow.calcTransferPrice = value.pricingData.price
            }
            if (currentRow.fromUnits && currentRow.toUnits && currentRow.rate) {
              //Ignore setting values
            } else {
              if (value.fromUnits != undefined) {
                currentRow.fromUnits = value.fromUnits
              }
              if (value.toUnits != undefined) {
                currentRow.toUnits = value.toUnits
              }
              if (value.rate != undefined) {
                currentRow.rate = value.rate
              }
            }
            if (value.balanceUnits != undefined) {
              currentRow.balanceUnits = value.balanceUnits
            }
          }
        },
      )
      this.isDeriving = false
    } catch (error) {
      this.errorHeader = TransactionConstants.ERROR_FETCH_DERIVED_FIELDS
      this.errorDetails = error.error
      this.isDeriving = false
    }
  }

  getGridTransactionsRequestforExchange(): BulkSecurityExchangeDerivedRequest[] {
    const transactions: BulkSecurityExchangeDerivedRequest[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(this.prepareBulkDerivedRequestData(rowNode.data))
    })
    return transactions
  }

  prepareBulkDerivedRequestData(
    rowData: BulkSecurityExchangeRow,
  ): BulkSecurityExchangeDerivedRequest {
    const transaction: BulkSecurityExchangeDerivedRequest = {
      rowId: Number(rowData.rowId),
      accountId: Number(rowData.accountId),
      fromUnits:
        rowData.fromUnits !== '' ? Number(rowData.fromUnits) : undefined,
      toUnits: rowData.toUnits !== '' ? Number(rowData.toUnits) : undefined,
      entryDate: rowData.entryDate !== '' ? rowData.entryDate : '',
      fromCusip: rowData.fromAssetId,
      toCusip: rowData.toAssetId,
      rate: rowData.rate !== '' ? Number(rowData.rate) : undefined,
      transferPrice:
        rowData.marketPrice !== '' ? Number(rowData.marketPrice) : undefined,
      inventoryType: rowData.inventoryType ? rowData.inventoryType : undefined,
    }
    return transaction
  }

  async validate() {
    this.gridApi?.api?.clearRangeSelection()
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        this.setMarketValueTextFromMvtSelected(
          rowNode.data.mvtBasisSelection,
          rowNode,
        )
      }
    })
    this.isDataValid = true
    this.isCommonValidationError = false
    if (this.markMissingAccountRows()) {
      return
    }
    const bulkValidateView = this.createBulkValidationView()
    this.isValidating = true
    this.errorDetails = ''
    try {
      this.loadingText = TransactionConstants.VALIDATE_TRANSACTION
      const validationInfos = await this.bulkTransactionService
        .validateBulkTransactionDirectTransfer(bulkValidateView)
        .toPromise()
      this.handleBulkValidationResponse(validationInfos.validationMap)
      this.handleCommonValidations(validationInfos.commonValidations)
      this.allDataValidated = true
      this.disableSaveValidRowsButton()
      this.isValidating = false
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  handleCommonValidations(commonValidations: ValidationInfo[]) {
    this.genericErrors = []
    this.genericWarnings = []
    this.currentNoteError = null
    if (commonValidations.length == 0) {
      this.showConfirmButtonModal = false
    }
    commonValidations.forEach(validationInfo => {
      if (validationInfo.impactedFields.length == 0) {
        validationInfo.impactedFields = ['generic']
      }
      validationInfo.impactedFields.forEach(field => {
        if (field == 'transactionNote') {
          this.isDataValid = false
          this.isCommonValidationError = true
          const validatorKey = 'transactionNoteValidator'
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.note.markAsTouched()
            this.currentNoteError = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
            this.note.setErrors(this.currentNoteError)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.noteWarning = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
          }
        } else if (
          field == 'confirmButton' &&
          validationInfo.level == ErrorSeverity.WARN
        ) {
          this.showConfirmButtonModal = true
        } else {
          //generic case
          this.isCommonValidationError = true
          this.isDataValid = false
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.genericErrors.push(validationInfo.message)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.genericWarnings.push(validationInfo.message)
          }
        }
      })
    })
  }

  handleBulkValidationResponse(bulkValidationInfos: {
    [key: number]: ValidationInfo[]
  }) {
    // create errorMessages column per row and update tooltip with it

    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        rowNode.data.validityStatus = 'valid'
        rowNode.data.errorMessageMap = new Map()
      }
    })

    this.note.setErrors(null)
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    const bulkValidationInfo = new Map<string, ValidationInfo[]>(
      Object.entries(bulkValidationInfos),
    )
    bulkValidationInfo.forEach((rowValidationInfo: ValidationInfo[], index) => {
      // create a map of key index,field and keep all errors(string or string[](if erros ) and warnings (if onlky warnings)
      // in cell class rule make it yellow if warning validity status
      // in tooltip use above map to put all erros in tooltip
      const currentRowNode = this.gridApi?.api.getRowNode(index)
      const errorMessages = new Map<string, string[]>()
      const warningMessages = new Map<string, string[]>()
      rowValidationInfo.forEach((validationInfo: ValidationInfo) => {
        const isError = validationInfo.level === ErrorSeverity.ERROR
        const messageMap = isError
          ? this.cellLevelErrorMessageMap
          : this.cellLevelWarningMessageMap
        const messages = isError ? errorMessages : warningMessages
        validationInfo.impactedFields.forEach((impactedField: string) => {
          const impactedFieldProcessed = this.deduceImpactedField(impactedField)
          const currentKey = `${index}-${impactedFieldProcessed}` // More efficient key representation
          // message map
          const msgList = messageMap.get(currentKey) || []
          msgList.push(validationInfo.message)
          messageMap.set(currentKey, msgList)
          // messages
          const fieldMessages = messages.get(impactedFieldProcessed) || []
          fieldMessages.push(validationInfo.message)
          messages.set(impactedFieldProcessed, fieldMessages)
        })
      })
      if (errorMessages.size > 0 && currentRowNode) {
        this.isDataValid = false
        currentRowNode.data.validityStatus = 'invalid'
        currentRowNode.data.errorMessageMap = errorMessages
      }
      if (
        errorMessages.size == 0 &&
        warningMessages.size > 0 &&
        currentRowNode
      ) {
        currentRowNode.data.validityStatus = 'warning'
        currentRowNode.data.errorMessageMap = warningMessages
      }

      if (rowValidationInfo.length == 0 && currentRowNode) {
        currentRowNode.data.validityStatus = 'valid'
        currentRowNode.data.errorMessageMap = new Map()
      }
    })
    this.gridApi?.api.redrawRows()
  }

  createBulkValidationView(): BulkSecurityExchangeValidationView {
    const view: BulkSecurityExchangeValidationView = {
      transaction: this.getGridTransactions(true),
      requestMode: RequestMode.NEW_TRAN,
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      tranCategory: TranCategory.TRANSFER,
    }
    if (this.note.value != '') view.transactionNote = this.note.value

    return view
  }

  getGridOnlyValidTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus == 'valid' ||
        rowNode.data.validityStatus == 'warning'
      )
        transactions.push(
          this.prepareBulkTransactionData(rowNode.data, isValidate),
        )
    })
    return transactions
  }

  getGridTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(
          this.prepareBulkTransactionData(rowNode.data, isValidate),
        )
    })
    return transactions
  }

  prepareBulkTransactionData(
    rowData: BulkSecurityExchangeRow,
    isValidate: boolean,
  ): TransactionRequestWrapper {
    const cp = getDefaultUnknownCounterParty()
    const transactionData: BulkSecurityExchangeTransaction = {
      units: this.updateUnit(Number(rowData.fromUnits), rowData),
      tranType: 'EXCHANGE_OUT',
      createdDate: new Date().toISOString(),
      userId: this.userId ?? 0,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: Number(rowData.accountId),
      entryDate: rowData.entryDate,
      settleDate: rowData.entryDate,
      postDate: rowData.entryDate,
      modifiedDate: new Date().toISOString(),
      dataMap: {},
      transferPurchaseInfoMap: {},
      active: true,
      rate: rowData.rate !== '' ? Number(rowData.rate) : undefined,
    }
    if (!isValidate) {
      if (this.cusipToSecurityMap) {
        transactionData.securityId = this.cusipToSecurityMap.get(
          rowData.fromAssetId,
        )
      }
      transactionData.revisionId = 1
      transactionData.counterParty = cp
      transactionData.counterPartyId = cp.id
    } else {
      transactionData.balanceUnits = rowData.balanceUnits
      transactionData.toUnits =
        rowData.toUnits !== '' ? Number(rowData.toUnits) : undefined
      transactionData.fromUnits =
        rowData.fromUnits !== '' ? Number(rowData.fromUnits) : undefined
    }
    if (rowData.inventoryType != '') {
      transactionData.inventoryType = rowData.inventoryType as InventoryType
    }
    if (rowData.marketPrice !== '') {
      transactionData.transferPrice = Number(rowData.marketPrice)
    }
    if (rowData.toAssetId !== '') {
      transactionData.destCusip = rowData.toAssetId
    }
    if (rowData.isConversion === 'True') {
      transactionData.exchangeType = ExchangeType.CONVERSION
    } else {
      transactionData.exchangeType = ExchangeType.CUSIP_EXCHANGE
    }

    if (
      (this.sessionReconDate &&
        !isAfter(
          new Date(rowData.entryDate),
          new Date(this.sessionReconDate),
        )) ||
      (!this.sessionReconDate &&
        this.selectedReconDate &&
        !isAfter(new Date(rowData.entryDate), new Date(this.selectedReconDate)))
    ) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    const metaData: MetaDataTransaction = {
      rowId: Number(rowData.rowId),
      cusip: rowData.fromAssetId,
      balanceUnits: rowData.balanceUnits,
      calcTransferPrice: rowData.calcTransferPrice,
      exchangeType: rowData.exchangeType,
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      destAccountId: Number(rowData.accountId),
      unitsRatio: rowData.rate !== '' ? Number(rowData.rate) : undefined,
      costBasisProportion: 1.0,
      destSecurityId: this.cusipToSecurityMap.get(rowData.toAssetId),
      inBases: rowData.mvtBasisSelection
        .filter(bases => bases.marketValue)
        .map(baseSelected => omit(baseSelected.basis, 'checked')) as Basis[],
    }

    const mvtData: MarketValueTransferModel = {
      category: TranCategory.DIRECT_TRANSFER,
      accountId: Number(rowData.accountId),
      tranType: 'EXCHANGE_OUT',
      entryDate: rowData.entryDate,
    }
    const mvtBasisExisting = this.mvtDataLoaded.get(JSON.stringify(mvtData))
    if (mvtBasisExisting) {
      metaData.mvtBases = mvtBasisExisting.mvtBases
    }

    return { transactionData, metaData }
  }

  updateUnit(unit: number, rowData: BulkSecurityExchangeRow) {
    if (rowData.inventoryType == 'SHORT') {
      return Math.abs(Number(unit))
    } else if (rowData.inventoryType == 'LONG') {
      return -1 * Math.abs(Number(unit))
    } else {
      return unit
    }
  }

  clearAll() {
    this.gridApi?.api.applyTransaction({
      remove: this.gridApi?.api.getRenderedNodes().map(rowNode => rowNode.data),
    })

    this.createFreshRowData()
    this.gridApi?.api.applyTransaction({ add: this.rowData })
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    this.errorDetails = ''
  }

  validateAndSave(isSaveValidRows?: boolean) {
    if (this.showConfirmButtonModal) {
      const modalRef = this.modalService.open(
        BulkTransactionsSaveConfirmationModalComponent,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          return isSaveValidRows ? this.saveValidRows() : this.save()
        } else return
      })
    } else if (isSaveValidRows) this.saveValidRows()
    else this.save()
  }

  async saveValidRows() {
    await this.save(true)
    const newRowData: BulkSecurityExchangeRow[] = []
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'valid' &&
        node.data.validityStatus != 'warning'
      )
        newRowData.push(node.data)
    })
    this.gridApi?.api.setRowData(newRowData)
    this.disableSaveValidRowsButton()
  }

  async save(onlyValidRows?: boolean) {
    if (!this.isDataValid && !onlyValidRows) {
      return
    }
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const tranCommand: BulkTransactionRequest = {
      transaction: onlyValidRows
        ? this.getGridOnlyValidTransactions(false)
        : this.getGridTransactions(false),
      tranCategory: TranCategory.DIRECT_TRANSFER,
      isDirectSecurityTransfer: true,
      requestMode: RequestMode.NEW_TRAN,
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    tranCommand.notes =
      TransactionConstants.HELIOS_BULK_SECURITY_EXCHANGE_SAVE_NOTE_CW_CREATE
    this.bulkTransactionService
      .createBulkTransaction(tranCommand, TranCategoryUrl.DIRECT_TRANSFER)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        () => {
          this.toastService.success(
            'Bulk Transaction create command successfully submitted.',
          )
          this.formSubmitting = false
          if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
        },
        error => {
          this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
          this.errorDetails = error.error
          this.formSubmitting = false
        },
      )
  }

  generateEmptyDataEntry(
    activated: boolean,
    id: number,
  ): BulkSecurityExchangeRow {
    return {
      rowId: id.toString(),
      accountId: '',
      entryDate: '',
      fromAssetId: '',
      toAssetId: '',
      marketPrice: '',
      fromUnits: '',
      toUnits: '',
      rate: activated ? '1' : '',
      exchangeType: activated ? statExchangeTypes[0].name : '',
      isConversion: activated ? 'False' : '',
      inventoryType: activated ? 'LONG' : '',
      mvtBasisSelection: [],
      mvtBasisSelectionText: activated
        ? TransactionConstants.MIN_FIELD_MESSAGE
        : '',
      validityStatus: activated ? 'intermediate' : 'inactive',
      errorMessageMap: new Map(),
    }
  }

  createFreshRowData() {
    this.rowData = []
    this.rowData.push(this.generateEmptyDataEntry(true, 1))
    for (let i = 1; i <= 9; i++) {
      this.rowData.push(this.generateEmptyDataEntry(false, i + 1))
    }
  }

  private closeModal(
    success: boolean,
    transactionRequests: TransactionRequestWrapper[],
  ): void {
    this.activeModal.close({
      success,
      transactionRequests,
    } as BulkTransactionModalResponse)
  }

  getPopUpRenderer(params: ICellRendererParams) {
    return {
      component: NgGridComponents.modalLinkRenderer,
      params: {
        modalComponent: BvMvBasisComponent,
        modalOptions: {
          size: 'md',
          windowClass: 'modal-window-click-through',
        } as NgbModalOptions,
        beforeOpen: () => this.shouldOpenMvtPicker(params.data),
        modalComplete: (mvtBasisSelectionUpdated: MvtBasisSelection[]) => {
          if (mvtBasisSelectionUpdated.length > 0)
            this.updateBasis(params.node, mvtBasisSelectionUpdated)
        },
      } as ModalLinkRendererParams,
    }
  }

  shouldOpenMvtPicker(data: BulkSecurityExchangeRow): boolean {
    return data.accountId != '' && data.entryDate != ''
  }

  updateBasis(node: RowNode, mvtBasisSelectionUpdated: MvtBasisSelection[]) {
    node.setDataValue('mvtBasisSelection', mvtBasisSelectionUpdated)
    this.setMarketValueTextFromMvtSelected(mvtBasisSelectionUpdated, node)
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  initializeMvtBasis(rowNode: RowNode, isMarketValueTextNotPasted: boolean) {
    const mvtData: MarketValueTransferModel = {
      category: TranCategory.DIRECT_TRANSFER,
      accountId: Number(rowNode.data.accountId),
      tranType: 'EXCHANGE_OUT',
      entryDate: rowNode.data.entryDate,
    }
    const mvtDataKey = JSON.stringify(mvtData)

    let mvtBasisPromise: Promise<MvtBasis>

    // Check to see if there is already a Promise for this request
    const mvtBasisExisting = this.mvtDataPromises.get(mvtDataKey)
    if (mvtBasisExisting) {
      mvtBasisPromise = mvtBasisExisting
    } else {
      // Create a new Promise for this request
      mvtBasisPromise = new Promise<MvtBasis>((resolve, reject) => {
        this.loadingBasis = true
        this.loadingText = TransactionConstants.LOAD_BASIS
        this.bulkTransactionService
          .loadMvtValues(mvtData)
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(
            mvtBasis => {
              this.loadingBasis = false
              resolve(mvtBasis) // Complete Promise with value from Observable
              this.mvtDataLoaded.set(mvtDataKey, mvtBasis)
            },
            err => {
              this.errorText = TransactionConstants.ERROR_LOAD_BASIS
              this.errorDetails = err.error
              reject(err)
            },
          )
      })

      // Add new Promise to map, for any future requests
      this.mvtDataPromises.set(mvtDataKey, mvtBasisPromise)
    }

    // Update row based on new or existing Promise
    mvtBasisPromise.then(mvtBasis => {
      this.setUpMvtBasis(rowNode, isMarketValueTextNotPasted, mvtBasis)
    })
  }

  setUpMvtBasis(
    rowNode: RowNode,
    isMarketValueTextNotPasted: boolean,
    mvtBasis: MvtBasis,
  ) {
    const allBasis = buildMvtBasisSelection(
      Number(rowNode.data.accountId),
      mvtBasis,
      false,
    )
    rowNode.data.mvtBasisSelection = allBasis
    if (isMarketValueTextNotPasted)
      this.setMarketValueTextFromMvtSelected(allBasis, rowNode)
  }

  setMarketValueTextFromMvtSelected(
    mvtBasisSelection: MvtBasisSelection[],
    rowNode: RowNode,
  ) {
    const marketValueBasis = mvtBasisSelection.filter(
      bases => bases.marketValue,
    )
    rowNode.setDataValue(
      'mvtBasisSelectionText',
      marketValueBasis.length == 0
        ? TransactionConstants.NO_MARKET_VALUE_TRANSFER
        : marketValueBasis.map(bases => bases.basis.prefix).join(),
    )
  }

  handleMvtSelectedChanged(rowNode: RowNode) {
    if (!this.shouldOpenMvtPicker(rowNode.data)) {
      rowNode.setDataValue(
        'mvtBasisSelectionText',
        TransactionConstants.MIN_FIELD_MESSAGE,
      )
      rowNode.data.mvtBasisSelection = []
    } else {
      if (
        rowNode.data.mvtBasisSelectionText ==
        TransactionConstants.NO_MARKET_VALUE_TRANSFER
      ) {
        rowNode.data.mvtBasisSelection.forEach(
          (mvtBasis: MvtBasisSelection) => {
            mvtBasis.marketValue = false
            mvtBasis.bookValue = true
          },
        )
      } else {
        const allMvBasisPrefix: string[] =
          rowNode.data.mvtBasisSelectionText.split(',')
        rowNode.data.mvtBasisSelection.forEach(
          (mvtBasis: MvtBasisSelection) => {
            mvtBasis.marketValue = allMvBasisPrefix.includes(
              mvtBasis.basis.prefix,
            )
            mvtBasis.bookValue = !allMvBasisPrefix.includes(
              mvtBasis.basis.prefix,
            )
          },
        )
      }
    }
  }

  copyHeaders() {
    const headers = this.gridApi?.columnApi
      .getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'validityStatus')
      .map(col => col.getColDef().headerName)
    let headersTabSeparated = ''
    if (headers) headersTabSeparated = headers.join('\t')

    navigator.clipboard.writeText(headersTabSeparated)
    this.toastService.success('Copied to Clipboard')
  }
}
